import { delay, map, tap } from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatError } from '@angular/material/form-field';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NgForm,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { AsyncPipe } from '@angular/common';

import {
  BundleOption,
  ProductBundleOptionType,
  BundledOptionGroup,
  TranslatePipe,
} from '@speedy/core';

import { SpeedyInputComponent } from '../../../../../shared/components/speedy-input/speedy-input.component';
import { CurrencyComponent } from '../../../../../shared/components/currency/currency.component';
import { SpeedySelectComponent } from '../../../../../shared/components/speedy-select/speedy-select.component';

interface OptionGroupForm {
  option_id: FormControl<string | null>;
  option_qty: FormControl<number | null>;
  option_selections: FormArray<FormControl<string | null>>;
  title: FormControl<string | null>;
  default_title: FormControl<string | null>;
  product_links: FormArray<
    FormGroup<{
      id: FormControl<string | null>;
      name: FormControl<string | null>;
      regular_price: FormControl<number | null>;
      checked: FormControl<boolean | null>;
      selection_qty: FormControl<string | null>;
      selection_can_change_qty: FormControl<string | null>;
    }>
  >;
  type: FormControl<string | null>;
  required: FormControl<number | null>;
  previous_option_selections: FormArray<FormControl<string | null>>;
}

@Component({
  selector: 'app-bundle-options',
  templateUrl: './bundle-options.component.html',
  styleUrls: ['./bundle-options.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SpeedySelectComponent,
    MatCheckbox,
    CurrencyComponent,
    MatError,
    MatRadioGroup,
    MatRadioButton,
    SpeedyInputComponent,
    TranslatePipe,
    AsyncPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BundleOptionsComponent {
  @ViewChild('form') ngForm!: NgForm;

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  options = input<BundleOption[]>();
  optionsChanged = output<BundledOptionGroup[]>();

  formOptions: FormArray<FormGroup<OptionGroupForm>>;
  formGroup: FormGroup;

  private bundledOptions: BundledOptionGroup[] = [];
  public showForm = signal<boolean>(false);

  ProductBundleOptionType = ProductBundleOptionType;

  constructor() {
    effect(
      () => {
        this.formOptions = this.generateFormArray();
        this.formGroup = this.fb.group({
          options: this.formOptions,
        });

        this.showForm.set(true);

        this.subscribeToFormChanges();
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  onSubmit(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
    }
  }

  private subscribeToFormChanges(): void {
    this.formGroup.controls.options.valueChanges
      .pipe(
        delay(100),
        map((options: BundledOptionGroup[]) =>
          options.map((option) => ({
            ...option,
            option_selections:
              option.type === ProductBundleOptionType.CHECKBOX
                ? option.product_links
                    .filter((link) => link.checked)
                    .map((link) => link.id)
                : option.option_selections,
          })),
        ),
        tap((options) => this.setOptionSelections(options)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const options = this.formGroup.controls.options.getRawValue();
        this.bundledOptions = options;
        this.optionsChanged.emit(options);
      });
  }

  private setOptionSelections(options: BundledOptionGroup[]): void {
    const optionsArray = this.formGroup.controls.options as FormArray<
      FormGroup<OptionGroupForm>
    >;

    optionsArray.value.forEach((option, id: number) => {
      if (optionsArray.controls[id].value.type === ProductBundleOptionType.CHECKBOX) {
        optionsArray.controls[id].controls.option_selections.patchValue(
          options[id].option_selections.length ? options[id].option_selections : [''],
          {
            emitEvent: false,
          },
        );
      }

      this.setOptionQtyBasedOnUserValue(options, optionsArray, id);
    });
  }

  private setOptionQtyBasedOnUserValue(
    options: BundledOptionGroup[],
    optionsArray: FormArray<FormGroup<OptionGroupForm>>,
    id: number,
  ): void {
    const optionGroup = options[id];
    const formGroup = optionsArray.controls[id];

    if (!optionGroup || !formGroup) {
      console.warn(`Invalid option group or form group at id: ${id}`);
      return;
    }

    const optionQtyControl = formGroup.get('option_qty');
    if (!optionQtyControl) {
      console.warn(`Missing 'option_qty' control at id: ${id}`);
      return;
    }

    const previousOptionSelections = formGroup.get('previous_option_selections')?.value;
    const optionsSelections = formGroup.get('option_selections')?.value;

    if (
      previousOptionSelections &&
      JSON.stringify(previousOptionSelections) === JSON.stringify(optionsSelections)
    ) {
      return;
    }

    formGroup.get('previous_option_selections')?.setValue(optionsSelections);

    const product = optionGroup.product_links.find((product) =>
      optionsSelections.includes(product.id),
    );

    if (product) {
      if (Number.parseInt(product.selection_can_change_qty) === 0) {
        optionQtyControl.disable({ emitEvent: false });
      } else {
        optionQtyControl.enable({ emitEvent: false });
      }

      const productQty = Number.parseInt(product.selection_qty);
      const optionQtyControlValue =
        typeof optionQtyControl.value === 'string'
          ? Number.parseInt(optionQtyControl.value)
          : optionQtyControl.value;

      if (
        optionQtyControlValue &&
        (productQty !== 1 || optionQtyControlValue !== productQty)
      ) {
        optionQtyControl.setValue(productQty, {
          emitEvent: false,
        });
      }
    } else {
      optionQtyControl.enable({ emitEvent: false });
    }
  }

  private generateFormArray(): FormArray<FormGroup> {
    const formArray: FormArray<FormGroup<OptionGroupForm>> = this.fb.array([
      ...this.options().map((option) => this.createFormGroup(option)),
    ]);

    return formArray;
  }

  private createFormGroup(option: BundleOption) {
    option.product_links.map(
      (product) => (product.name += `, +${product.regular_price}`),
    );

    option.product_links.filter((option) => option.id);

    const formControls: OptionGroupForm = {
      option_id: this.fb.control(option.option_id, Validators.required),
      option_qty: this.fb.control(1, Validators.required),
      option_selections: this.fb.array([
        this.fb.control(
          '',
          Number.parseInt(option.required) ? Validators.required : null,
        ),
      ]),
      title: this.fb.control(option.title),
      default_title: this.fb.control(option.default_title),
      product_links: this.fb.array(
        option.product_links.map((p) =>
          this.fb.group({
            id: this.fb.control(p.id),
            name: this.fb.control(p.name),
            regular_price: this.fb.control(p.regular_price),
            selection_qty: this.fb.control(p.selection_qty),
            selection_can_change_qty: this.fb.control(p.selection_can_change_qty),
            checked: this.fb.control(false),
          }),
        ),
      ),
      type: this.fb.control(option.type),
      required: this.fb.control(Number.parseInt(option.required)),
      previous_option_selections: this.fb.array([this.fb.control('')]),
    };

    return this.fb.group(formControls);
  }

  getBundleOptions(): BundledOptionGroup[] {
    return this.bundledOptions;
  }

  validate(): boolean {
    this.ngForm.ngSubmit.emit();
    return this.formGroup.valid;
  }
}
